import {
  ALL_THEMES,
  computeMergedTheme,
  type BrandTheme,
  type MergedTheme,
} from '@owner/theme'
import get from 'lodash/get'

import globalStore from '@/store'

import type { ExtendedMergedTheme } from './extendedTheme'
import { extractAbsoluteNeutralColors } from './extendedTheme'

/**
 * Gets the brand theme from either a local resource or the active brand
 * @param brandThemePath - Optional path to theme in local resource (e.g., 'theme')
 * @param storeModule - Optional store module name (e.g., 'websiteBuilder')
 * @returns BrandTheme or undefined
 */
function getBrandTheme(
  brandThemePath: string | undefined,
  storeModule: string | undefined
): BrandTheme | undefined {
  // Try to get theme from local resource (e.g., draft being edited)
  if (brandThemePath && storeModule) {
    const currentResource =
      globalStore.getters[`${storeModule}/currentResource`]
    const localTheme = get(currentResource, brandThemePath) as
      | BrandTheme
      | undefined

    if (localTheme) {
      return localTheme
    }
  }

  // Fall back to active brand theme using store getter
  return globalStore.getters['core/brand/active/theme'] as
    | BrandTheme
    | undefined
}

/**
 * Gets the merged theme for a brand theme
 * Tries to use cached store getter for active brand, otherwise computes it
 * @param brandTheme - The brand theme to get merged theme for
 * @returns MergedTheme
 */
function getMergedTheme(brandTheme: BrandTheme): MergedTheme {
  const activeBrandTheme = globalStore.getters['core/brand/active/theme'] as
    | BrandTheme
    | undefined

  // If this is the active brand's theme, use the cached merged theme from store
  if (
    activeBrandTheme &&
    activeBrandTheme.brandId === brandTheme.brandId &&
    activeBrandTheme.baseTheme === brandTheme.baseTheme
  ) {
    const cachedMergedTheme = globalStore.getters[
      'core/brand/active/mergedTheme'
    ] as MergedTheme | undefined

    if (cachedMergedTheme) {
      return cachedMergedTheme
    }
  }

  // Otherwise compute the merged theme
  return computeMergedTheme(brandTheme)
}

/**
 * Extends a merged theme with absolute neutral colors
 * @param mergedTheme - The merged theme to extend
 * @param baseThemeName - The base theme name (e.g., 'light', 'dark')
 * @returns Extended merged theme with absolute colors
 */
function extendMergedTheme(
  mergedTheme: MergedTheme,
  baseThemeName: string
): ExtendedMergedTheme {
  const baseTheme = ALL_THEMES[baseThemeName as keyof typeof ALL_THEMES]
  const absoluteNeutralColors = extractAbsoluteNeutralColors(
    baseTheme.config.colors
  )

  return {
    ...mergedTheme,
    absolute: {
      neutral: absoluteNeutralColors,
    },
  }
}

/**
 * Gets the brand's merged theme, extended with absolute colors
 * Supports both local resource themes and active brand theme
 * @param brandThemePath - Optional path to theme in local resource
 * @param storeModule - Optional store module name
 * @returns Extended merged theme or undefined
 */
export function createExtendedTheme(
  brandThemePath: string | undefined,
  storeModule: string | undefined
): ExtendedMergedTheme | undefined {
  const brandTheme = getBrandTheme(brandThemePath, storeModule)

  if (!brandTheme) {
    return undefined
  }

  const mergedTheme = getMergedTheme(brandTheme)

  return extendMergedTheme(mergedTheme, brandTheme.baseTheme)
}
